
import Icon from "@/components/UI/Icon.vue";
import { jsonrepair } from "jsonrepair";
import { Component, Prop, Vue } from "vue-property-decorator";
import {
  cleanJson,
  cleanJsonBeforeParse,
  getOnboardingEventSource,
  getTextFromChunk
} from "@/lib/ai/api";
import {
  getElementDescription,
  getElementFriendlyName,
  getElementQualifier,
  getElementTemperature,
  getFieldsToComplete,
  handleAiError,
  processCompletion,
  saveToElementMetadata,
  updateDocument
} from "@/lib/ai/ai";
import { formatFieldsForOpenAI, getPrompt } from "@/lib/ai/prompts";
import {
  configStore,
  documentStore,
  globalStore,
  onboardingStore
} from "@/store/store";
import { ChatMessage, FieldToComplete, PromptInfo } from "@/lib/ai/types";
import { TreeNode } from "@/types/tree";
import { getWebsiteDetailsAsQualifier } from "@/subapps/onboarding/lib/ai";
import { getOnboardingSettings } from "@/subapps/onboarding/lib/settings";
import { saveToWordPressWithErrorsLoggedToUiStore } from "@/lib/wordpress/save";

@Component({
  components: {
    Icon
  }
})
export default class AIPanel extends Vue {
  rewriteInProgress = false;
  savingSettings = false;
  loadingSettings = false;
  pluginNotInstalled = false;

  maxRetries = 3;
  retries = 0;

  @Prop()
  element!: TreeNode;

  get elementConfig() {
    return this.element ? configStore.element(this.element.data.type) : null;
  }

  get isAIDisabled() {
    return (
      this.elementConfig?.settings && this.elementConfig?.settings.disableAI
    );
  }

  get settings() {
    return globalStore.aiSettings;
  }

  get websiteSettings() {
    return onboardingStore.websiteDetails;
  }

  get prompt(): PromptInfo {
    return {
      sectionDescription: this.elementDescription,
      qualifiers: this.qualifiers,
      temperature: this.temperature,
      lockedFields: [],
      locale: this.settings.locale,
      unsplash: {
        enabled: false,
        fillEmptyImagesOnly: false,
        keywords: ""
      }
    };
  }

  get friendlyName() {
    if (!this.element) return "Section";
    return getElementFriendlyName(this.element);
  }

  get elementDescription() {
    if (!this.element) return "";
    return getElementDescription(this.element);
  }

  get qualifiers() {
    if (!this.element) return "";
    const elementQualifier = getElementQualifier(this.element);
    const websiteDetailsQualifier = getWebsiteDetailsAsQualifier(
      this.websiteSettings
    );
    return `${elementQualifier} ${websiteDetailsQualifier}`;
  }

  get temperature() {
    if (!this.element) return 1;
    return getElementTemperature(this.element);
  }

  async streamRewrite(prompt: ChatMessage[], fields: FieldToComplete[]) {
    const eventSource = getOnboardingEventSource(prompt);
    let parts = "";

    if (!eventSource) {
      throw new Error("No event source was returned from OpenAI API.");
    }

    return new Promise((resolve, reject) => {
      eventSource.onmessage = async event => {
        if (event.data === "[DONE]") {
          eventSource.close();

          try {
            const repaired = jsonrepair(parts);
            const parsedCompletion = processCompletion(repaired, fields);

            updateDocument(parsedCompletion.elements);
            console.log("%cOutput", "color: green;");
            console.log(repaired);
            resolve(parts);
          } catch (error) {
            reject(error);
          }
        } else {
          parts += getTextFromChunk(event.data);
          parts = cleanJson(parts);

          if (!parts) return;

          try {
            const cleanJson = cleanJsonBeforeParse(parts);
            const repaired = jsonrepair(cleanJson);
            const parsedCompletion = processCompletion(repaired, fields);

            updateDocument(parsedCompletion.elements);
          } catch (error) {
            // Parsing is allowed to fail here, we'll just try again in the next chunk.
            console.debug(parts);
            console.debug(error);
          }
        }
      };

      eventSource.onerror = event => {
        eventSource.close();
        reject((event as MessageEvent).data);
      };
    });
  }

  async rewrite() {
    if (this.rewriteInProgress) return;
    if (!this.element) return;

    const fields = getFieldsToComplete(this.element);
    const fieldsToFill = formatFieldsForOpenAI(fields);
    const prompt = getPrompt(fieldsToFill, this.prompt, this.settings.prompt);

    this.rewriteInProgress = true;
    saveToElementMetadata(this.element, this.prompt);

    console.log("%cInput", "color: #2196F3;");
    console.log({ fields, prompt });

    if (!fields.length) {
      this.rewriteInProgress = false;
      return;
    }

    try {
      await this.streamRewrite(prompt, fields);
      await saveToWordPressWithErrorsLoggedToUiStore();
    } catch (error) {
      await this.retry(error);
    }

    this.rewriteInProgress = false;
  }

  async retry(error: unknown) {
    this.rewriteInProgress = false;

    if (this.retries < this.maxRetries) {
      console.log(
        `%cRetrying ${this.retries + 1} of ${this.maxRetries}`,
        "color: #2196F3;"
      );
      this.retries++;
      await this.rewrite();
    } else {
      this.retries = 0;
      console.log("Max retries reached, giving up.");
      this.handleError(error as string);
    }
  }

  handleError(error: string) {
    handleAiError(error);
  }

  updatePrompt(prompt: PromptInfo) {
    if (!this.element) return;

    documentStore.unthrottledPropertyChanged({
      elementId: this.element.id,
      path: "meta.promptInfo",
      meta: {
        snapshotLabel: `Updated prompt info for element #${this.element.id}`
      },
      value: prompt
    });
  }

  async beforeMount() {
    await getOnboardingSettings();
  }
}
